/**
 * USASpending Data Fetcher
 *
 * Fetches USASpending data from the API endpoints.
 * All functions take an access token as the first parameter.
 */
import { API_URL } from '@/constants/env'

import {
  type RecipientLevel,
  buildRecipientId,
  selectRecipientLevel,
} from '@/lib/usaspending/recipientId'

import type { FederalContract } from '@/types/federal-contract/FederalContract'
import type {
  AgencyProfile,
  Award,
  ContractSolicitation,
  PersonProfile,
  RecipientAwardSummary,
  RecipientInsight,
  RecipientProfile,
  Solicitation,
  SubAward,
  SubAwardDetail,
  TopCategory,
  Transaction,
} from '@/types/usaspending'
// ============================================================================
// Analytics Endpoints (Dashboard Data)
// ============================================================================
import type {
  AwardCategory,
  AwardPortfolioResponse,
  CategoryBreakdown,
  CompetitionBreakdown,
  ContractSizeHistogram,
  GeographicCityDistribution,
  GeographicDistribution,
  IdvGroupTimeSeriesResponse,
  IdvParentItem,
  RecipientAnalyticsFilters,
  SetAsideBreakdown,
  TimeSeriesPoint,
} from '@/types/usaspending'
// --- Recipient Dashboard (Batched) ---
import type { RecipientDashboard } from '@/types/usaspending/analytics'

/**
 * API response types matching the server schemas
 */
export interface ApiAwardListItem {
  award_id: number
  display_award_id: string
  generated_unique_award_id: string
  category: string
  type: string
  type_description: string
  award_amount: number | null
  recipient_name: string | null
  recipient_hash: string | null
  recipient_uei: string | null
  recipient_levels: string[]
  parent_uei: string | null
  awarding_toptier_agency_name: string | null
  action_date: string | null
  fiscal_year: number | null
  description: string | null
}

export interface ApiRecipientListItem {
  id: number
  recipient_hash: string
  recipient_name: string
  recipient_level: string
  uei: string | null
  parent_uei: string | null
  last_12_months: number
  last_12_months_count: number
}

export interface ApiAgencyListItem {
  toptier_agency_id: number
  toptier_code: string
  name: string
  abbreviation: string | null
  mission: string | null
  website: string | null
  icon_filename: string | null
}

/**
 * Internal API response types for transform functions
 */
/** API response for per-DEFC breakdown */
interface ApiDefcBreakdown {
  code: string
  description?: string
  obligation: number
  outlay: number
}

/** API response for DEFC data */
interface ApiDefcData {
  codes?: string[]
  breakdowns?: ApiDefcBreakdown[]
  total_covid19_obligation?: number
  total_covid19_outlay?: number
  total_iija_obligation?: number
  total_iija_outlay?: number
}

interface ApiAwardDetailResponse {
  award_id?: number
  generated_unique_award_id?: string
  piid?: string
  fain?: string
  uri?: string
  parent_award_piid?: string
  parent_award_generated_unique_id?: string
  description?: string
  category?: 'contract' | 'grant' | 'other'
  type?: string
  type_description?: string
  classification?: {
    type_of_contract_pricing?: string
    naics_code?: string
    naics_description?: string
    product_or_service_code?: string
    product_or_service_description?: string
    cfda_number?: string
    cfda_title?: string
    cfdas?: string[]
  }
  awarding_agency?: {
    toptier_code?: string
    toptier_name?: string
    subtier_name?: string
    office_name?: string
  }
  funding_agency?: {
    toptier_code?: string
    toptier_name?: string
    subtier_name?: string
    office_name?: string
  }
  recipient?: {
    recipient_hash?: string
    recipient_name?: string
    recipient_uei?: string
    parent_uei?: string
    recipient_levels?: string[]
    location?: {
      city?: string
      state_code?: string
      zip5?: string
      country_name?: string
    }
    business_categories?: string[]
  }
  financials?: {
    total_obligation?: number
    award_amount?: number
    base_and_all_options_value?: number
    base_exercised_options_val?: number
    non_federal_funding_amount?: number
    total_outlays?: number
    // Loan-specific
    total_loan_value?: number
    total_subsidy_cost?: number
    face_value_loan_guarantee?: number
    // Disaster Emergency Fund Code (DEFC) data
    defc_data?: ApiDefcData
  }
  dates?: {
    period_of_performance_start_date?: string
    period_of_performance_current_end_date?: string
    ordering_period_end_date?: string
    last_modified_date?: string
    date_signed?: string
    action_date?: string
  }
  place_of_performance?: {
    city?: string
    state_code?: string
    zip5?: string
    country_name?: string
  }
  // Procurement details (FPDS contracts only)
  procurement_details?: {
    extent_competed?: string
    extent_competed_description?: string
    other_than_full_and_open_competition?: string
    other_than_full_and_open_competition_description?: string
    fair_opportunity_limited_sources?: string
    fair_opportunity_limited_sources_description?: string
    type_set_aside?: string
    type_set_aside_description?: string
    solicitation_identifier?: string
    solicitation_procedures?: string
    solicitation_procedures_description?: string
    solicitation_date?: string
    number_of_offers_received?: string
    type_of_contract_pricing?: string
    type_of_contract_pricing_description?: string
    cost_or_pricing_data?: string
    cost_or_pricing_data_description?: string
    commercial_item_acquisition?: string
    commercial_item_acquisition_description?: string
    commercial_item_test_program?: string
    commercial_item_test_program_description?: string
    multi_year_contract?: string
    multi_year_contract_description?: string
    consolidated_contract?: string
    consolidated_contract_description?: string
    purchase_card_as_payment_method?: string
    purchase_card_as_payment_method_description?: string
    labor_standards?: string
    labor_standards_description?: string
    construction_wage_rate_requirement?: string
    construction_wage_rate_requirement_description?: string
    domestic_or_foreign_entity?: string
    domestic_or_foreign_entity_description?: string
    foreign_funding?: string
    foreign_funding_description?: string
    interagency_contracting_authority?: string
    interagency_contracting_authority_description?: string
  }
  // Parent IDV details (contract orders only)
  parent_award?: {
    piid?: string
    generated_unique_award_id?: string
    idv_type?: string
    idv_type_description?: string
    multiple_or_single_award?: string
    multiple_or_single_award_description?: string
    type_of_idc?: string
    type_of_idc_description?: string
    awarding_agency?: {
      toptier_name?: string
      toptier_code?: string
      subtier_name?: string
      subtier_code?: string
    }
  }
  // Program details (assistance awards only: grants, loans, etc.)
  program_details?: {
    program_number?: string
    program_title?: string
    popular_name?: string
    federal_agency?: string
    objectives?: string
    types_of_assistance?: string
    applicant_eligibility?: string
    beneficiary_eligibility?: string
    application_procedures?: string
    deadlines?: string
    website_address?: string
    sam_url?: string
  }
}

interface ApiRecipientCertifications {
  woman_owned?: boolean
  veteran_owned?: boolean
  sdvosb?: boolean
  is_8a?: boolean
  hubzone?: boolean
  sdb?: boolean
  minority_owned?: boolean
  small_business?: boolean
  alaska_native?: boolean
  native_american?: boolean
  nonprofit?: boolean
  educational?: boolean
}

interface ApiRecipientDetailResponse {
  recipient_id?: string
  recipient_level?: string
  recipient_hash?: string
  legal_business_name?: string
  uei?: string
  duns?: string
  cage_code?: string
  address?: {
    line_1?: string
    city?: string
    state?: string
    zip5?: string
    country_code?: string
    congressional_district?: string
  }
  business_types_codes?: string[]
  parent?: {
    name?: string
    uei?: string
    recipient_hash?: string
  }
  officers?: { name: string; amount?: number }[] | null
  certifications?: ApiRecipientCertifications
  deferred_fields?: string[]
}

interface ApiAgencyDetailResponse {
  toptier_code?: string
  toptier_name?: string
  subtier_name?: string
  icon_filename?: string
  website?: string
  mission?: string
  stats?: {
    fiscal_year_obligations?: string
    contract_count?: number
    grant_count?: number
    top_naics_code?: string
  }
}

interface ApiPersonDetailResponse {
  id?: string
  full_name?: string
  title?: string
  email?: string
  phone?: string
  fax?: string
  agencies?: {
    toptier_code?: string
    name?: string
    sub_tier?: string
    office?: string
  }[]
}

interface ApiOpportunityResponse {
  id?: string | number
  source?: 'SAM.gov' | 'Grants.gov' | 'DIBBS'
  solicitation_number?: string
  title?: string
  description?: string
  posted_date?: string
  response_deadline?: string
  active_until?: string
  link?: string
  nsn_number?: string
  psc_code?: string
  notice_type?: string
  naics_code?: string
  naics_industry?: string
  set_aside_code?: string
}

/**
 * Shared response types for analytics and list data
 */

/** Analytics category item (name, amount, percentage) */
interface ApiAnalyticsCategoryItem {
  name: string
  agency_code?: string
  amount: number
  percentage: number
}

/** Analytics category item with code (for PSC, NAICS) */
interface ApiAnalyticsCodeCategoryItem {
  code: string
  description: string
  amount: number
  percentage: number
}

/** Optional analytics category item (for agency analytics where fields may be null) */
interface ApiOptionalAnalyticsCategoryItem {
  name?: string
  recipient_hash?: string
  amount?: number
  percentage?: number
}

/** Optional analytics code category item (for agency NAICS analytics) */
interface ApiOptionalCodeCategoryItem {
  name?: string
  code?: string
  amount?: number
  percentage?: number
}

/** Transaction item from award transactions endpoint */
interface ApiTransactionItem {
  modification_number: string
  action_date: string
  action_type: string
  federal_action_obligation: number
  transaction_description: string
  // Procurement-specific fields (FPDS)
  type_of_contract_pricing: string | null
  type_of_contract_pricing_description: string | null
  type_set_aside: string | null
  type_set_aside_description: string | null
  extent_competed: string | null
  extent_competed_description: string | null
  // NAICS/PSC codes
  naics_code: string | null
  naics_description: string | null
  product_or_service_code: string | null
  product_or_service_description: string | null
  // Awarding/funding offices
  awarding_office_code: string | null
  awarding_office_name: string | null
  funding_office_code: string | null
  funding_office_name: string | null
  // Flag to indicate if this is a procurement (FPDS) transaction
  is_fpds: boolean | null
}

/** Subaward item from award subawards endpoint */
interface ApiSubawardItem {
  subaward_id: string | null
  subaward_number: string
  sub_action_date: string
  subaward_amount: number
  sub_awardee_or_recipient_legal: string
  sub_awardee_or_recipient_uei: string | null
  sub_awardee_recipient_hash: string | null
  subaward_description: string
  // Place of performance
  sub_place_of_perform_city_name: string | null
  sub_place_of_perform_state_code: string | null
  // Subawardee business location
  sub_legal_entity_city_name: string | null
  sub_legal_entity_state_code: string | null
  // Additional details
  sub_business_types: string | null // Comma-separated
  subaward_type: string | null
  sub_naics: string | null
  naics_description: string | null
}

/** Subaward detail response from subaward detail endpoint */
interface ApiSubawardDetailResponse {
  subaward_id: string
  subaward_number: string
  internal_id?: string
  subaward_type?: string
  subaward_amount: number
  sub_action_date: string
  subaward_description?: string
  sub_fiscal_year: number

  subawardee: {
    name?: string
    uei?: string
    duns?: string
    recipient_hash?: string
    dba_name?: string
    parent_name?: string
    parent_uei?: string
    location: {
      city?: string
      state_code?: string
      state_name?: string
      county_name?: string
      zip5?: string
      country_code?: string
      country_name?: string
      congressional_district?: string
    }
    business_types: string[]
  }

  subawardee_officers: Array<{
    name: string
    amount?: number
  }>

  prime_award: {
    award_id?: number
    unique_award_key?: string
    piid?: string
    fain?: string
    award_amount?: number
    award_description?: string
    prime_award_type?: string
    prime_award_group?: string
  }

  prime_contractor: {
    name?: string
    uei?: string
    duns?: string
    recipient_hash?: string
    dba_name?: string
    parent_name?: string
    parent_uei?: string
    location: {
      city?: string
      state_code?: string
      state_name?: string
      county_name?: string
      zip5?: string
      country_code?: string
      country_name?: string
      congressional_district?: string
    }
  }

  awarding_agency: {
    toptier_name?: string
    toptier_code?: string
    subtier_name?: string
    subtier_code?: string
    office_name?: string
  }

  funding_agency: {
    toptier_name?: string
    toptier_code?: string
    subtier_name?: string
    subtier_code?: string
    office_name?: string
  }

  place_of_performance: {
    city?: string
    state_code?: string
    state_name?: string
    county_name?: string
    zip5?: string
    country_code?: string
    country_name?: string
    congressional_district?: string
  }

  classification: {
    naics_code?: string
    naics_description?: string
    product_or_service_code?: string
    product_or_service_description?: string
    cfda_number?: string
    cfda_title?: string
    cfdas?: string[]
    type_of_contract_pricing?: string
    type_set_aside?: string
    extent_competed?: string
  }

  report_year?: number
  report_month?: number
  date_submitted?: string
  last_modified_date?: string
}

/** Federal account item from award federal accounts endpoint */
interface ApiFederalAccountItem {
  id: string
  title: string | null
  agency_identifier: string | null
  main_account_code: string | null
}

/** Recipient award summary item */
interface ApiRecipientAwardItem {
  award_id: number
  display_award_id: string
  generated_unique_award_id: string
  type: string
  type_description: string
  award_amount: number | null
  awarding_toptier_agency_name: string | null
  action_date: string | null
  description: string | null
}

// Helper to create headers with auth token.
// When token is empty, the Authorization header is omitted so the request
// hits the backend's `maybe_auth_user_id` path (unauthenticated).
function createHeaders(token: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

/** Structured API error with status code for reliable error detection */
export class ApiError extends Error {
  readonly status: number
  readonly detail: string
  constructor(status: number, statusText: string, detail?: string) {
    super(`API error: ${status} ${detail ?? statusText}`)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail ?? statusText
  }
}

/** Retryable 503 error for ClickHouse waking up */
export class ServiceWakingError extends Error {
  readonly status = 503
  readonly retryAfter: number
  readonly retryReason: string

  constructor(retryAfter: number, retryReason: string) {
    super(`Service is waking up (${retryReason}), retry after ${retryAfter}s`)
    this.name = 'ServiceWakingError'
    this.retryAfter = retryAfter
    this.retryReason = retryReason
  }
}

// Helper to fetch JSON with auth
async function fetchJson<T>(token: string, url: string): Promise<T> {
  const res = await fetch(url, { headers: createHeaders(token) })

  // Handle 503 with X-Retry-Reason header (e.g., ClickHouse waking up)
  if (res.status === 503) {
    const retryReason = res.headers.get('X-Retry-Reason')
    if (retryReason === 'clickhouse-waking') {
      const retryAfter = parseInt(res.headers.get('Retry-After') || '5', 10)
      throw new ServiceWakingError(retryAfter, retryReason)
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, res.statusText)
  }
  return res.json()
}

// Award Endpoints

export async function fetchAward(token: string, id: string): Promise<Award> {
  interface AwardWithSolicitationResponse extends ApiAwardDetailResponse {
    linked_solicitation?: {
      id: string
      source: 'SAM.gov' | 'Grants.gov' | 'DIBBS'
      solicitation_number?: string
      title?: string
    }
  }

  interface FederalAccountsResponse {
    award_id: number
    results: ApiFederalAccountItem[]
  }

  // First, try to get award with solicitation linking info
  const response = await fetchJson<AwardWithSolicitationResponse>(
    token,
    `${API_URL}/api/v1/usaspending/awards/${id}/with-solicitation`,
  )

  // Transform base award data
  const award = transformAwardDetailResponse(response)

  // Fetch federal accounts (use award_id from response for numeric ID)
  const awardId = response.award_id || id
  try {
    const accountsResponse = await fetchJson<FederalAccountsResponse>(
      token,
      `${API_URL}/api/v1/usaspending/awards/${awardId}/federal-accounts`,
    )
    award.federalAccounts = accountsResponse.results.map((acc) => ({
      id: acc.id,
      title: acc.title || '',
      agencyIdentifier: acc.agency_identifier || '',
      mainAccountCode: acc.main_account_code || '',
      accountType: 'Appropriated',
    }))
  } catch (e) {
    console.warn('Failed to fetch federal accounts:', e)
  }

  // Check if there's a linked solicitation
  const linkedSolicitation = response.linked_solicitation

  if (linkedSolicitation?.id && linkedSolicitation?.source === 'SAM.gov') {
    try {
      const fcResponse = await fetch(
        `${API_URL}/api/v1/federal-contracts/${linkedSolicitation.id}`,
        { headers: createHeaders(token) },
      )
      if (fcResponse.ok) {
        const federalContract: FederalContract = await fcResponse.json()
        const solicitation =
          transformFederalContractToSolicitation(federalContract)
        if (solicitation) {
          award.solicitation = solicitation
        }
      }
    } catch (e) {
      console.warn('Failed to fetch full solicitation data:', e)
    }
  }

  return award
}

export async function fetchAwardTransactions(
  token: string,
  awardId: string,
  page: number = 1,
  limit: number = 25,
): Promise<{ data: Transaction[]; total: number; hasMore: boolean }> {
  const response = await fetchJson<{
    results: ApiTransactionItem[]
    total: number
    has_next: boolean
  }>(
    token,
    `${API_URL}/api/v1/usaspending/awards/${awardId}/transactions?page=${page}&limit=${limit}`,
  )

  return {
    data: response.results.map((t) => ({
      modNumber: t.modification_number,
      actionDate: t.action_date,
      actionType: t.action_type,
      federalActionObligation: t.federal_action_obligation,
      description: t.transaction_description,
      // Procurement-specific fields (FPDS)
      typeOfContractPricing: t.type_of_contract_pricing ?? undefined,
      typeOfContractPricingDescription:
        t.type_of_contract_pricing_description ?? undefined,
      typeSetAside: t.type_set_aside ?? undefined,
      typeSetAsideDescription: t.type_set_aside_description ?? undefined,
      extentCompeted: t.extent_competed ?? undefined,
      extentCompetedDescription: t.extent_competed_description ?? undefined,
      // NAICS/PSC
      naicsCode: t.naics_code ?? undefined,
      naicsDescription: t.naics_description ?? undefined,
      productOrServiceCode: t.product_or_service_code ?? undefined,
      productOrServiceDescription:
        t.product_or_service_description ?? undefined,
      // Awarding/funding offices
      awardingOfficeCode: t.awarding_office_code ?? undefined,
      awardingOfficeName: t.awarding_office_name ?? undefined,
      fundingOfficeCode: t.funding_office_code ?? undefined,
      fundingOfficeName: t.funding_office_name ?? undefined,
      isFpds: t.is_fpds ?? undefined,
    })),
    total: response.total,
    hasMore: response.has_next,
  }
}

export async function fetchAwardSubawards(
  token: string,
  awardId: string,
  page: number = 1,
  limit: number = 25,
): Promise<{ data: SubAward[]; total: number; hasMore: boolean }> {
  const response = await fetchJson<{
    results: ApiSubawardItem[]
    total: number
    has_next: boolean
  }>(
    token,
    `${API_URL}/api/v1/usaspending/awards/${awardId}/subawards?page=${page}&limit=${limit}`,
  )

  return {
    data: response.results.map((s) => ({
      id: s.subaward_number,
      subawardId: s.subaward_id ?? undefined,
      subAwardee: {
        id: buildRecipientId(s.sub_awardee_recipient_hash) ?? undefined,
        name: s.sub_awardee_or_recipient_legal || '',
        uei: s.sub_awardee_or_recipient_uei || '',
        // Business location (where subawardee is located)
        location: {
          city: s.sub_legal_entity_city_name || '',
          state: s.sub_legal_entity_state_code || '',
          zip: '',
          country: 'USA',
        },
        // Parse comma-separated business types
        businessTypes: s.sub_business_types
          ? s.sub_business_types
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : [],
      },
      amount: s.subaward_amount,
      date: s.sub_action_date,
      description: s.subaward_description || '',
      // Place of performance (where work is done)
      placeOfPerformance: {
        city: s.sub_place_of_perform_city_name || undefined,
        state: s.sub_place_of_perform_state_code || undefined,
      },
      subawardType: s.subaward_type || undefined,
      naicsCode: s.sub_naics || undefined,
      naicsDescription: s.naics_description || undefined,
    })),
    total: response.total,
    hasMore: response.has_next,
  }
}

/**
 * Order item from award orders endpoint (child awards under an IDV)
 * Extends ApiAwardListItem with optional obligation/outlay fields.
 *
 * Note: Backend should be updated to include total_obligation and total_outlays
 * in the orders response for accurate IDV aggregation.
 */
export interface ApiOrderItem extends ApiAwardListItem {
  /** Total obligated amount (if available from backend) */
  total_obligation?: number | null
  /** Total outlayed amount (if available from backend) */
  total_outlays?: number | null
}

export async function fetchAwardOrders(
  token: string,
  awardId: string,
  page: number = 1,
  limit: number = 25,
): Promise<{ data: ApiOrderItem[]; total: number; hasMore: boolean }> {
  const response = await fetchJson<{
    parent_piid: string
    results: ApiOrderItem[]
    total: number
    has_next: boolean
  }>(
    token,
    `${API_URL}/api/v1/usaspending/awards/${awardId}/orders?page=${page}&limit=${limit}`,
  )

  return {
    data: response.results,
    total: response.total,
    hasMore: response.has_next,
  }
}

/**
 * IDV child aggregation response from the backend
 */
export interface ApiIdvChildAggregation {
  parent_piid: string
  total_orders: number
  total_obligation: number
  total_outlays: number
}

/**
 * Fetch aggregated financial totals from IDV child orders
 *
 * Returns the sum of obligations and outlays from all child orders under an IDV.
 * This enables meaningful progress display for IDV awards where obligations
 * occur at the child order level rather than the master contract level.
 *
 * @param token - Access token
 * @param awardId - The IDV award ID
 */
export async function fetchIdvChildAggregation(
  token: string,
  awardId: string,
): Promise<ApiIdvChildAggregation> {
  const response = await fetchJson<ApiIdvChildAggregation>(
    token,
    `${API_URL}/api/v1/usaspending/awards/${awardId}/orders/aggregation`,
  )
  return response
}

/**
 * IDV activity node for Sankey visualization
 */
export interface ApiIdvActivityNode {
  id: string
  award_id: number
  piid: string | null
  category: string
  type: string | null
  type_description: string | null
  recipient_name: string | null
  recipient_hash: string | null
  awarding_agency: string | null
  total_obligation: number | null
  action_date: string | null
  level: number // 0 = parent, 1 = child, 2 = grandchild
  is_grandchild: boolean
}

/**
 * IDV activity link for Sankey visualization
 */
export interface ApiIdvActivityLink {
  source: string
  target: string
  value: number | null
}

/**
 * IDV activity summary statistics
 *
 * Includes both USASpending's pre-computed counts (from parent_award table)
 * and the actual counts we found in our database. The difference represents
 * missing data due to incomplete parent-child linkages.
 */
export interface ApiIdvActivitySummary {
  // USASpending's pre-computed counts
  direct_idv_count: number
  direct_contract_count: number
  direct_total_obligation: number
  rollup_idv_count: number
  rollup_contract_count: number
  rollup_total_obligation: number

  // Actual counts we found
  actual_direct_count: number
  actual_rollup_count: number
  actual_total_obligation: number

  // When USASpending last computed the parent_award data
  computed_at: string | null
}

/**
 * IDV activity response from the backend
 */
export interface ApiIdvActivityResponse {
  parent_award_id: number
  parent_piid: string
  parent_unique_id: string
  summary: ApiIdvActivitySummary
  nodes: ApiIdvActivityNode[]
  links: ApiIdvActivityLink[]
}

/**
 * Fetch IDV activity hierarchy for Sankey visualization
 *
 * Returns parent IDV, child awards, and grandchild awards
 * in a format suitable for @nivo/sankey visualization.
 *
 * @param token - Access token
 * @param awardId - The IDV award ID
 */
export async function fetchIdvActivity(
  token: string,
  awardId: string,
): Promise<ApiIdvActivityResponse> {
  const response = await fetchJson<ApiIdvActivityResponse>(
    token,
    `${API_URL}/api/v1/usaspending/awards/${awardId}/activity`,
  )
  return response
}

/**
 * Batch check if awards exist by generated_unique_award_id
 *
 * Used by the DataLoader pattern for efficient N->1 API calls.
 *
 * @param token - Access token
 * @param awardIds - Array of generated_unique_award_ids to check
 */
export async function checkAwardsExist(
  token: string,
  awardIds: string[],
): Promise<Record<string, boolean>> {
  const res = await fetch(`${API_URL}/api/v1/usaspending/awards/check-exist`, {
    method: 'POST',
    headers: createHeaders(token),
    body: JSON.stringify({ award_ids: awardIds }),
  })

  if (!res.ok) {
    throw new ApiError(res.status, res.statusText)
  }

  const response = (await res.json()) as { results: Record<string, boolean> }
  return response.results
}

// ============================================================================
// Subaward Endpoints
// ============================================================================

export async function fetchSubaward(
  token: string,
  subawardId: string,
): Promise<SubAwardDetail> {
  const response = await fetchJson<ApiSubawardDetailResponse>(
    token,
    `${API_URL}/api/v1/usaspending/subawards/${encodeURIComponent(subawardId)}`,
  )
  return transformSubawardDetailResponse(response)
}

// ============================================================================
// Recipient Endpoints
// ============================================================================

export async function fetchRecipient(
  token: string,
  id: string,
): Promise<RecipientProfile> {
  const [
    profileResponse,
    statsResponse,
    agenciesResponse,
    pscResponse,
    awardsResponse,
  ] = await Promise.all([
    fetchJson<ApiRecipientDetailResponse>(
      token,
      `${API_URL}/api/v1/usaspending/recipients/${id}`,
    ),
    fetchJson<{
      total_prime_awards: number
      total_obligated_amount: number
      last_transaction_date: string | null
    }>(token, `${API_URL}/api/v1/usaspending/recipients/${id}/stats`).catch(
      () => ({
        total_prime_awards: 0,
        total_obligated_amount: 0,
        last_transaction_date: null,
      }),
    ),
    fetchJson<{
      results: ApiAnalyticsCategoryItem[]
    }>(
      token,
      `${API_URL}/api/v1/usaspending/recipients/${id}/analytics/agencies?limit=15`,
    ).catch(() => ({ results: [] })),
    fetchJson<{
      results: ApiAnalyticsCodeCategoryItem[]
    }>(
      token,
      `${API_URL}/api/v1/usaspending/recipients/${id}/analytics/psc?limit=5`,
    ).catch(() => ({ results: [] })),
    fetchJson<{
      results: ApiRecipientAwardItem[]
    }>(
      token,
      `${API_URL}/api/v1/usaspending/recipients/${id}/awards?limit=10`,
    ).catch(() => ({ results: [] })),
  ])

  const profile = transformRecipientDetailResponse(profileResponse)

  profile.stats = {
    totalPrimeAwards: statsResponse.total_prime_awards,
    totalObligatedAmount: statsResponse.total_obligated_amount,
    lastTransactionDate: statsResponse.last_transaction_date || '',
  }

  profile.topAgencies = agenciesResponse.results.map((a) => ({
    name: a.name,
    amount: a.amount,
    percentage: a.percentage,
  }))

  profile.topPscCodes = pscResponse.results.map((p) => ({
    name: p.description || p.code,
    code: p.code,
    amount: p.amount,
    percentage: p.percentage,
  }))

  profile.recentAwards = awardsResponse.results.map((a) => ({
    id: a.generated_unique_award_id,
    piid: a.display_award_id || '',
    date: a.action_date || '',
    agency: a.awarding_toptier_agency_name || '',
    type: a.type_description || a.type || '',
    amount: a.award_amount || 0,
    description: a.description || '',
  }))

  return profile
}

export async function fetchRecipientProfile(
  token: string,
  id: string,
): Promise<RecipientProfile> {
  // Use enhanced endpoint to get certifications along with basic profile
  const response = await fetchJson<ApiRecipientDetailResponse>(
    token,
    `${API_URL}/api/v1/usaspending/recipients/${id}/enhanced`,
  )
  return transformRecipientDetailResponse(response)
}

interface ApiRecipientBasicInfoResponse {
  recipient_id?: string
  recipient_level?: string
  recipient_hash: string
  available_levels?: string[]
  self_referential?: boolean
  parent?: {
    name?: string | null
    uei?: string | null
    recipient_hash?: string | null
  } | null
  legal_business_name?: string
  uei?: string
  duns?: string
  address?: {
    line_1?: string
    city?: string
    state?: string
    zip5?: string
    country_code?: string
  }
}

export async function fetchRecipientBasicInfo(
  token: string,
  id: string,
): Promise<RecipientProfile> {
  const response = await fetchJson<ApiRecipientBasicInfoResponse>(
    token,
    `${API_URL}/api/v1/usaspending/recipients/${id}/basic-info`,
  )
  return {
    id:
      response.recipient_id ??
      buildRecipientId(response.recipient_hash, response.recipient_level) ??
      '',
    name: response.legal_business_name ?? '',
    uei: response.uei ?? '',
    duns: response.duns,
    availableLevels: (response.available_levels ?? []) as RecipientLevel[],
    selfReferential: response.self_referential ?? false,
    parent: response.parent
      ? {
          name: response.parent.name ?? '',
          // Parent link defaults to P-level; may 404 if entity lacks a P record (rare)
          id: buildRecipientId(response.parent.recipient_hash, 'P'),
        }
      : undefined,
    // cageCode loaded separately from profile endpoint (progressive loading)
    location: {
      streetAddress: response.address?.line_1,
      city: response.address?.city ?? '',
      state: response.address?.state ?? '',
      zip: response.address?.zip5 ?? '',
      country: response.address?.country_code ?? 'USA',
    },
    businessTypes: [],
    stats: undefined,
    topAgencies: [],
    topPscCodes: [],
    recentAwards: [],
  }
}

export async function fetchRecipientStats(
  token: string,
  id: string,
  fiscalYear?: number,
): Promise<{
  totalPrimeAwards: number
  totalObligatedAmount: number
  totalContractAmount?: number
  lastTransactionDate: string
}> {
  const url = new URL(`${API_URL}/api/v1/usaspending/recipients/${id}/stats`)
  appendRecipientFiscalYear(url, fiscalYear)

  const response = await fetchJson<{
    total_prime_awards: number
    total_obligated_amount: number
    total_contract_amount?: number
    last_transaction_date: string | null
  }>(token, url.toString())
  return {
    totalPrimeAwards: response.total_prime_awards,
    totalObligatedAmount: response.total_obligated_amount,
    totalContractAmount: response.total_contract_amount,
    lastTransactionDate: response.last_transaction_date || '',
  }
}

export async function fetchRecipientAnalyticsAgencies(
  token: string,
  id: string,
  fiscalYear?: number,
): Promise<TopCategory[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${id}/analytics/agencies`,
  )
  url.searchParams.set('limit', '15')
  appendRecipientFiscalYear(url, fiscalYear)

  const response = await fetchJson<{
    results: ApiAnalyticsCategoryItem[]
  }>(token, url.toString())
  return response.results.map((a) => ({
    name: a.name,
    code: a.agency_code,
    amount: a.amount,
    percentage: a.percentage,
  }))
}

export async function fetchRecipientAnalyticsPsc(
  token: string,
  id: string,
  fiscalYear?: number,
): Promise<(TopCategory & { code: string })[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${id}/analytics/psc`,
  )
  url.searchParams.set('limit', '15')
  appendRecipientFiscalYear(url, fiscalYear)

  const response = await fetchJson<{
    results: ApiAnalyticsCodeCategoryItem[]
  }>(token, url.toString())
  return response.results.map((p) => ({
    name: p.description || p.code,
    code: p.code,
    amount: p.amount,
    percentage: p.percentage,
  }))
}

export async function fetchRecipientAnalyticsNaics(
  token: string,
  id: string,
  fiscalYear?: number,
): Promise<(TopCategory & { code: string })[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${id}/analytics/naics`,
  )
  url.searchParams.set('limit', '15')
  appendRecipientFiscalYear(url, fiscalYear)

  const response = await fetchJson<{
    results: ApiAnalyticsCodeCategoryItem[]
  }>(token, url.toString())
  return response.results.map((n) => ({
    name: n.description || n.code,
    code: n.code,
    amount: n.amount,
    percentage: n.percentage,
  }))
}

export async function fetchRecipientRecentAwards(
  token: string,
  id: string,
  limit: number = 10,
): Promise<RecipientAwardSummary[]> {
  const response = await fetchJson<{
    results: ApiRecipientAwardItem[]
  }>(
    token,
    `${API_URL}/api/v1/usaspending/recipients/${id}/awards?limit=${limit}`,
  )
  return response.results.map((a) => ({
    id: a.generated_unique_award_id,
    piid: a.display_award_id || '',
    date: a.action_date || '',
    agency: a.awarding_toptier_agency_name || '',
    type: a.type_description || a.type || '',
    amount: a.award_amount || 0,
    description: a.description || '',
  }))
}

/** Hierarchy node in the org tree */
export interface ApiHierarchyNode {
  recipient_hash: string
  recipient_id: string | null
  name: string | null
  uei: string | null
  state: string | null
  city: string | null
  last_12_months: number | null
  subsidiary_count: number
}

/** Hierarchy response from the backend */
export interface ApiRecipientHierarchyResponse {
  self_node: ApiHierarchyNode
  parent: ApiHierarchyNode | null
  children: ApiHierarchyNode[]
  siblings: ApiHierarchyNode[]
  total_children: number
  total_siblings: number
}

/** Frontend-friendly hierarchy types */
export interface RecipientHierarchyNode {
  recipientHash: string
  recipientId: string | null
  name: string | null
  uei: string | null
  state: string | null
  city: string | null
  last12Months: number | null
  subsidiaryCount: number
}

export interface RecipientHierarchy {
  selfNode: RecipientHierarchyNode
  parent: RecipientHierarchyNode | null
  children: RecipientHierarchyNode[]
  siblings: RecipientHierarchyNode[]
  totalChildren: number
  totalSiblings: number
}

function transformHierarchyNode(
  node: ApiHierarchyNode,
): RecipientHierarchyNode {
  return {
    recipientHash: node.recipient_hash,
    recipientId: node.recipient_id,
    name: node.name,
    uei: node.uei,
    state: node.state,
    city: node.city,
    last12Months: node.last_12_months,
    subsidiaryCount: node.subsidiary_count,
  }
}

export async function fetchRecipientHierarchy(
  token: string,
  recipientHash: string,
): Promise<RecipientHierarchy> {
  const response = await fetchJson<ApiRecipientHierarchyResponse>(
    token,
    `${API_URL}/api/v1/recipients/${recipientHash}/hierarchy`,
  )
  return {
    selfNode: transformHierarchyNode(response.self_node),
    parent: response.parent ? transformHierarchyNode(response.parent) : null,
    children: response.children.map(transformHierarchyNode),
    siblings: response.siblings.map(transformHierarchyNode),
    totalChildren: response.total_children,
    totalSiblings: response.total_siblings,
  }
}

export async function fetchRecipientInsights(
  token: string,
  id: string,
  limit: number = 4,
  fiscalYear?: number,
): Promise<RecipientInsight[]> {
  const url = new URL(`${API_URL}/api/v1/usaspending/recipients/${id}/insights`)
  url.searchParams.set('limit', String(limit))
  appendRecipientFiscalYear(url, fiscalYear)

  const response = await fetchJson<{
    recipient_hash: string
    insights: RecipientInsight[]
  }>(token, url.toString())
  return response.insights
}

export async function fetchRecipientOfficers(
  token: string,
  id: string,
): Promise<{ fullName: string; compensation?: number }[]> {
  const response = await fetchJson<{
    officers: { name: string; amount?: number }[]
  }>(token, `${API_URL}/api/v1/usaspending/recipients/${id}/officers`)
  return response.officers.map((o) => ({
    fullName: o.name,
    compensation: o.amount,
  }))
}

// ============================================================================
// Agency Endpoints
// ============================================================================

export async function fetchAgency(
  token: string,
  id: string,
): Promise<AgencyProfile> {
  const [
    profileResponse,
    recipientsResponse,
    naicsResponse,
    opportunitiesResponse,
  ] = await Promise.all([
    fetchJson<ApiAgencyDetailResponse>(
      token,
      `${API_URL}/api/v1/usaspending/agencies/${id}`,
    ),
    fetchJson<{
      results: ApiOptionalAnalyticsCategoryItem[]
    }>(
      token,
      `${API_URL}/api/v1/usaspending/agencies/${id}/analytics/recipients`,
    ).catch(() => ({ results: [] })),
    fetchJson<{
      results: ApiOptionalCodeCategoryItem[]
    }>(
      token,
      `${API_URL}/api/v1/usaspending/agencies/${id}/analytics/naics`,
    ).catch(() => ({ results: [] })),
    fetchJson<{ results: ApiOpportunityResponse[] }>(
      token,
      `${API_URL}/api/v1/usaspending/agencies/${id}/opportunities?limit=5`,
    ).catch(() => ({ results: [] })),
  ])

  const profile = transformAgencyDetailResponse(profileResponse)

  profile.topRecipients = recipientsResponse.results.map((r) => ({
    name: r.name ?? '',
    code: r.recipient_hash ?? undefined,
    amount: r.amount ?? 0,
    percentage: r.percentage ?? 0,
  }))

  profile.spendByNaics = naicsResponse.results.map((n) => ({
    name: n.name ?? '',
    code: n.code ?? '',
    amount: n.amount ?? 0,
    percentage: n.percentage ?? 0,
  }))

  profile.recentOpportunities = opportunitiesResponse.results.map((o) =>
    transformOpportunityToSolicitation(o),
  )

  return profile
}

export async function fetchAgencyProfile(
  token: string,
  id: string,
): Promise<AgencyProfile> {
  const response = await fetchJson<ApiAgencyDetailResponse>(
    token,
    `${API_URL}/api/v1/usaspending/agencies/${id}`,
  )
  return transformAgencyDetailResponse(response)
}

/** API response for basic agency info (fast, RDS-only) */
interface ApiAgencyBasicInfoResponse {
  agency_id: number
  toptier_code?: string
  toptier_name?: string
  subtier_code?: string
  subtier_name?: string
  abbreviation?: string
  mission?: string
  website?: string
  icon_filename?: string
}

/**
 * Fetch basic agency info (fast, RDS-only).
 * Returns only the profile data without analytics - used by header section.
 */
export async function fetchAgencyBasicInfo(
  token: string,
  id: string,
): Promise<AgencyProfile> {
  const response = await fetchJson<ApiAgencyBasicInfoResponse>(
    token,
    `${API_URL}/api/v1/usaspending/agencies/${id}/basic-info`,
  )
  // Transform to AgencyProfile with undefined stats
  return {
    id: response.toptier_code ?? '',
    name: response.toptier_name ?? '',
    subTier: response.subtier_name ?? '',
    office: '',
    logoUrl: response.icon_filename,
    website: response.website,
    description: response.mission,
    stats: undefined,
    topRecipients: [],
    spendByNaics: [],
    recentOpportunities: [],
  }
}

export async function fetchAgencyAnalyticsRecipients(
  token: string,
  id: string,
): Promise<TopCategory[]> {
  const response = await fetchJson<{
    results: ApiOptionalAnalyticsCategoryItem[]
  }>(token, `${API_URL}/api/v1/usaspending/agencies/${id}/analytics/recipients`)
  return response.results.map((r) => ({
    name: r.name ?? '',
    code: r.recipient_hash ?? undefined,
    amount: r.amount ?? 0,
    percentage: r.percentage ?? 0,
  }))
}

export async function fetchAgencyAnalyticsNaics(
  token: string,
  id: string,
): Promise<(TopCategory & { code: string })[]> {
  const response = await fetchJson<{
    results: ApiOptionalCodeCategoryItem[]
  }>(token, `${API_URL}/api/v1/usaspending/agencies/${id}/analytics/naics`)
  return response.results.map((n) => ({
    name: n.name ?? '',
    code: n.code ?? '',
    amount: n.amount ?? 0,
    percentage: n.percentage ?? 0,
  }))
}

export async function fetchAgencyOpportunities(
  token: string,
  id: string,
  limit: number = 5,
): Promise<Solicitation[]> {
  const response = await fetchJson<{
    results: ApiOpportunityResponse[]
  }>(
    token,
    `${API_URL}/api/v1/usaspending/agencies/${id}/opportunities?limit=${limit}`,
  )
  return response.results.map((o) => transformOpportunityToSolicitation(o))
}

// ============================================================================
// Person Endpoints
// ============================================================================

export async function fetchPerson(
  token: string,
  id: string,
): Promise<PersonProfile> {
  const response = await fetchJson<ApiPersonDetailResponse>(
    token,
    `${API_URL}/api/v1/usaspending/people/${id}/with-agency`,
  )
  return transformPersonDetailResponse(response)
}

// ============================================================================
// Transform Functions
// ============================================================================

function transformFederalContractToSolicitation(
  fc: FederalContract,
): ContractSolicitation | undefined {
  if (fc.notices.length === 0) {
    return undefined
  }

  const latestNotice = fc.notices.reduce((latest, n) =>
    new Date(n.posted_date) > new Date(latest.posted_date) ? n : latest,
  )

  return {
    source: 'SAM.gov',
    id: fc.id,
    solicitationNumber: latestNotice?.solicitation_number || '',
    title: latestNotice?.title || 'Federal Contract Opportunity',
    description: fc.ai_summary || latestNotice?.description || '',
    postedDate: latestNotice?.posted_date || '',
    responseDeadline: latestNotice?.response_deadline || '',
    archiveDate: latestNotice?.archive_date || '',
    link: latestNotice?.link || '',
    noticeType: latestNotice?.notice_type || '',
    naics: {
      code: latestNotice?.naics_code || '',
      name: latestNotice?.naics_industry || '',
    },
    psc: {
      code: latestNotice?.psc_code || '',
      name: latestNotice?.psc_industry || '',
    },
    setAside:
      latestNotice?.set_aside_description ||
      latestNotice?.set_aside_code ||
      'None',
    office: latestNotice?.office || '',
    placeOfPerformance: {
      city: latestNotice?.place_of_performance_city || '',
      state: latestNotice?.place_of_performance_state || '',
      zip: latestNotice?.place_of_performance_zip || '',
      country: latestNotice?.place_of_performance_country || 'USA',
    },
    pointOfContact: {
      fullName: latestNotice?.primary_contact_full_name || 'Not Available',
      email: latestNotice?.primary_contact_email || '',
      phone: latestNotice?.primary_contact_phone || '',
      title: latestNotice?.primary_contact_title || '',
    },
    notices: fc.notices.map((n) => ({
      id: n.id,
      type: n.notice_type || 'Notice',
      date: n.posted_date,
      title: n.title || '',
    })),
    files: fc.files.map((f) => ({
      id: f.id,
      name: f.name,
      size: f.size ? `${Math.round(f.size / 1024)} KB` : 'Unknown',
      type: f.name.split('.').pop()?.toUpperCase() || 'FILE',
      uploadedDate: f.created_at || '',
      url: f.url || '',
    })),
  }
}

function transformAwardDetailResponse(r: ApiAwardDetailResponse): Award {
  return {
    id: String(r.award_id ?? ''),
    generatedUniqueAwardId: r.generated_unique_award_id ?? '',
    piid: r.piid ?? '',
    fain: r.fain,
    uri: r.uri,
    parentAwardPiid: r.parent_award_piid,
    parentAwardGeneratedUniqueId: r.parent_award_generated_unique_id,
    title: r.description ?? 'Untitled Award',
    description: r.description ?? '',
    category: r.category ?? 'other',
    type: r.type ?? '',
    typeDescription: r.type_description ?? '',
    pricingType: r.classification?.type_of_contract_pricing ?? '',
    awardingAgency: {
      id: r.awarding_agency?.toptier_code ?? '',
      name: r.awarding_agency?.toptier_name ?? '',
      subTier: r.awarding_agency?.subtier_name ?? '',
      office: r.awarding_agency?.office_name ?? '',
    },
    fundingAgency: {
      id: r.funding_agency?.toptier_code ?? '',
      name: r.funding_agency?.toptier_name ?? '',
      subTier: r.funding_agency?.subtier_name ?? '',
      office: r.funding_agency?.office_name ?? '',
    },
    recipient: {
      id:
        buildRecipientId(
          r.recipient?.recipient_hash,
          selectRecipientLevel(
            r.recipient?.recipient_levels,
            r.recipient?.parent_uei,
          ),
        ) ?? '',
      name: r.recipient?.recipient_name ?? '',
      uei: r.recipient?.recipient_uei ?? '',
      location: {
        city: r.recipient?.location?.city ?? '',
        state: r.recipient?.location?.state_code ?? '',
        zip: r.recipient?.location?.zip5 ?? '',
        country: r.recipient?.location?.country_name ?? 'USA',
      },
      businessTypes: r.recipient?.business_categories ?? [],
    },
    financials: {
      obligatedAmount: r.financials?.total_obligation ?? 0,
      currentAwardAmount: r.financials?.award_amount ?? 0,
      potentialAwardAmount: r.financials?.base_and_all_options_value ?? 0,
      nonFederalFunding: r.financials?.non_federal_funding_amount ?? 0,
      totalOutlayedAmount: r.financials?.total_outlays ?? 0,
      // IDV-specific
      baseAndAllOptionsValue: r.financials?.base_and_all_options_value,
      baseExercisedOptionsValue: r.financials?.base_exercised_options_val,
      // Loan-specific
      totalLoanValue: r.financials?.total_loan_value,
      totalSubsidyCost: r.financials?.total_subsidy_cost,
      faceValueLoanGuarantee: r.financials?.face_value_loan_guarantee,
      // Generic award amount
      awardAmount: r.financials?.award_amount,
      // DEFC (Disaster Emergency Fund Code) data
      defcData: r.financials?.defc_data
        ? {
            codes: r.financials.defc_data.codes ?? [],
            breakdowns:
              r.financials.defc_data.breakdowns?.map((b) => ({
                code: b.code,
                description: b.description,
                obligation: b.obligation,
                outlay: b.outlay,
              })) ?? [],
            totalCovid19Obligation:
              r.financials.defc_data.total_covid19_obligation,
            totalCovid19Outlay: r.financials.defc_data.total_covid19_outlay,
            totalIijaObligation: r.financials.defc_data.total_iija_obligation,
            totalIijaOutlay: r.financials.defc_data.total_iija_outlay,
          }
        : undefined,
    },
    federalAccounts: [],
    dates: {
      startDate: r.dates?.period_of_performance_start_date ?? '',
      endDate: r.dates?.period_of_performance_current_end_date ?? '',
      potentialEndDate: r.dates?.ordering_period_end_date ?? '',
      lastModifiedDate: r.dates?.last_modified_date ?? '',
      dateSigned: r.dates?.date_signed ?? '',
      actionDate: r.dates?.action_date ?? '',
    },
    placeOfPerformance: {
      city: r.place_of_performance?.city ?? '',
      state: r.place_of_performance?.state_code ?? '',
      zip: r.place_of_performance?.zip5 ?? '',
      country: r.place_of_performance?.country_name ?? 'USA',
    },
    codes: {
      naicsCode: r.classification?.naics_code ?? '',
      naicsDescription: r.classification?.naics_description ?? '',
      pscCode: r.classification?.product_or_service_code ?? '',
      pscDescription: r.classification?.product_or_service_description ?? '',
      cfdaNumber: r.classification?.cfda_number ?? undefined,
      cfdaTitle: r.classification?.cfda_title ?? undefined,
      cfdaNumbers: r.classification?.cfdas ?? undefined,
    },
    history: [],
    subAwards: [],
    solicitation: undefined,
    // Procurement details (FPDS contracts only)
    procurementDetails: r.procurement_details
      ? {
          extentCompeted: r.procurement_details.extent_competed,
          extentCompetedDescription:
            r.procurement_details.extent_competed_description,
          otherThanFullAndOpenCompetition:
            r.procurement_details.other_than_full_and_open_competition,
          otherThanFullAndOpenCompetitionDescription:
            r.procurement_details
              .other_than_full_and_open_competition_description,
          fairOpportunityLimitedSources:
            r.procurement_details.fair_opportunity_limited_sources,
          fairOpportunityLimitedSourcesDescription:
            r.procurement_details.fair_opportunity_limited_sources_description,
          typeSetAside: r.procurement_details.type_set_aside,
          typeSetAsideDescription:
            r.procurement_details.type_set_aside_description,
          solicitationIdentifier: r.procurement_details.solicitation_identifier,
          solicitationProcedures: r.procurement_details.solicitation_procedures,
          solicitationProceduresDescription:
            r.procurement_details.solicitation_procedures_description,
          solicitationDate: r.procurement_details.solicitation_date,
          numberOfOffersReceived:
            r.procurement_details.number_of_offers_received,
          typeOfContractPricing: r.procurement_details.type_of_contract_pricing,
          typeOfContractPricingDescription:
            r.procurement_details.type_of_contract_pricing_description,
          costOrPricingData: r.procurement_details.cost_or_pricing_data,
          costOrPricingDataDescription:
            r.procurement_details.cost_or_pricing_data_description,
          commercialItemAcquisition:
            r.procurement_details.commercial_item_acquisition,
          commercialItemAcquisitionDescription:
            r.procurement_details.commercial_item_acquisition_description,
          commercialItemTestProgram:
            r.procurement_details.commercial_item_test_program,
          commercialItemTestProgramDescription:
            r.procurement_details.commercial_item_test_program_description,
          multiYearContract: r.procurement_details.multi_year_contract,
          multiYearContractDescription:
            r.procurement_details.multi_year_contract_description,
          consolidatedContract: r.procurement_details.consolidated_contract,
          consolidatedContractDescription:
            r.procurement_details.consolidated_contract_description,
          purchaseCardAsPaymentMethod:
            r.procurement_details.purchase_card_as_payment_method,
          purchaseCardAsPaymentMethodDescription:
            r.procurement_details.purchase_card_as_payment_method_description,
          laborStandards: r.procurement_details.labor_standards,
          laborStandardsDescription:
            r.procurement_details.labor_standards_description,
          constructionWageRateRequirement:
            r.procurement_details.construction_wage_rate_requirement,
          constructionWageRateRequirementDescription:
            r.procurement_details
              .construction_wage_rate_requirement_description,
          domesticOrForeignEntity:
            r.procurement_details.domestic_or_foreign_entity,
          domesticOrForeignEntityDescription:
            r.procurement_details.domestic_or_foreign_entity_description,
          foreignFunding: r.procurement_details.foreign_funding,
          foreignFundingDescription:
            r.procurement_details.foreign_funding_description,
          interagencyContractingAuthority:
            r.procurement_details.interagency_contracting_authority,
          interagencyContractingAuthorityDescription:
            r.procurement_details.interagency_contracting_authority_description,
        }
      : undefined,
    // Parent IDV details (contract orders only)
    parentAward: r.parent_award
      ? {
          piid: r.parent_award.piid,
          generatedUniqueAwardId: r.parent_award.generated_unique_award_id,
          idvType: r.parent_award.idv_type,
          idvTypeDescription: r.parent_award.idv_type_description,
          multipleOrSingleAward: r.parent_award.multiple_or_single_award,
          multipleOrSingleAwardDescription:
            r.parent_award.multiple_or_single_award_description,
          typeOfIdc: r.parent_award.type_of_idc,
          typeOfIdcDescription: r.parent_award.type_of_idc_description,
          awardingAgency: r.parent_award.awarding_agency
            ? {
                toptierName: r.parent_award.awarding_agency.toptier_name,
                toptierCode: r.parent_award.awarding_agency.toptier_code,
                subtierName: r.parent_award.awarding_agency.subtier_name,
                subtierCode: r.parent_award.awarding_agency.subtier_code,
              }
            : undefined,
        }
      : undefined,
    // Program details (assistance awards only: grants, loans, etc.)
    programDetails: r.program_details
      ? {
          programNumber: r.program_details.program_number,
          programTitle: r.program_details.program_title,
          popularName: r.program_details.popular_name,
          federalAgency: r.program_details.federal_agency,
          objectives: r.program_details.objectives,
          typesOfAssistance: r.program_details.types_of_assistance,
          applicantEligibility: r.program_details.applicant_eligibility,
          beneficiaryEligibility: r.program_details.beneficiary_eligibility,
          applicationProcedures: r.program_details.application_procedures,
          deadlines: r.program_details.deadlines,
          websiteAddress: r.program_details.website_address,
          samUrl: r.program_details.sam_url,
        }
      : undefined,
  }
}

function transformRecipientDetailResponse(
  r: ApiRecipientDetailResponse,
): RecipientProfile {
  return {
    id:
      r.recipient_id ??
      buildRecipientId(r.recipient_hash, r.recipient_level) ??
      '',
    name: r.legal_business_name ?? '',
    uei: r.uei ?? '',
    duns: r.duns,
    cageCode: r.cage_code,
    location: {
      streetAddress: r.address?.line_1,
      city: r.address?.city ?? '',
      state: r.address?.state ?? '',
      zip: r.address?.zip5 ?? '',
      country: r.address?.country_code ?? 'USA',
      congressionalDistrict: r.address?.congressional_district,
    },
    businessTypes: r.business_types_codes ?? [],
    certifications: r.certifications
      ? {
          womanOwned: r.certifications.woman_owned,
          veteranOwned: r.certifications.veteran_owned,
          sdvosb: r.certifications.sdvosb,
          is8a: r.certifications.is_8a,
          hubzone: r.certifications.hubzone,
          sdb: r.certifications.sdb,
          minorityOwned: r.certifications.minority_owned,
          smallBusiness: r.certifications.small_business,
          alaskaNative: r.certifications.alaska_native,
          nativeAmerican: r.certifications.native_american,
          nonprofit: r.certifications.nonprofit,
          educational: r.certifications.educational,
        }
      : undefined,
    parent: r.parent
      ? {
          name: r.parent.name ?? '',
          id: buildRecipientId(r.parent.recipient_hash, 'P'),
        }
      : undefined,
    officers:
      r.officers === null
        ? undefined
        : (r.officers?.map((o) => ({
            fullName: o.name,
            compensation: o.amount,
            email: '',
          })) ?? []),
    deferredFields: r.deferred_fields ?? [],
    stats: undefined,
    topAgencies: [],
    topPscCodes: [],
    recentAwards: [],
  }
}

function transformAgencyDetailResponse(
  r: ApiAgencyDetailResponse,
): AgencyProfile {
  return {
    id: r.toptier_code ?? '',
    name: r.toptier_name ?? '',
    subTier: r.subtier_name ?? '',
    office: '',
    logoUrl: r.icon_filename,
    website: r.website,
    description: r.mission,
    stats: r.stats
      ? {
          fiscalYearObligations:
            parseFloat(r.stats.fiscal_year_obligations ?? '0') || 0,
          contractCount: r.stats.contract_count ?? 0,
          grantCount: r.stats.grant_count ?? 0,
          topNaicsCode: r.stats.top_naics_code ?? '',
        }
      : undefined,
    topRecipients: [],
    spendByNaics: [],
    recentOpportunities: [],
  }
}

function transformPersonDetailResponse(
  r: ApiPersonDetailResponse,
): PersonProfile {
  const agency = r.agencies?.[0]
  return {
    id: r.id ?? '',
    fullName: r.full_name ?? '',
    title: r.title,
    email: r.email ?? '',
    phone: r.phone,
    fax: r.fax,
    agency: {
      id: agency?.toptier_code ?? '',
      name: agency?.name ?? '',
      subTier: agency?.sub_tier ?? '',
      office: agency?.office ?? '',
    },
    activities: [],
  }
}

function transformOpportunityToSolicitation(
  o: ApiOpportunityResponse,
): Solicitation {
  const source = o.source ?? 'SAM.gov'
  const baseSolicitation = {
    id: String(o.id ?? ''),
    solicitationNumber: o.solicitation_number ?? '',
    title: o.title ?? '',
    description: o.description ?? '',
    postedDate: o.posted_date ?? '',
    responseDeadline: o.response_deadline ?? '',
    archiveDate: o.active_until ?? '',
    link: o.link ?? '',
    pointOfContact: {
      fullName: '',
      email: '',
    },
    notices: [],
    files: [],
  }

  if (source === 'DIBBS') {
    return {
      ...baseSolicitation,
      source: 'DIBBS' as const,
      nsnNumber: o.nsn_number ?? '',
      status: 'open',
      nomenclature: o.title ?? '',
      techDocsAvailable: false,
      deliveryFob: '',
      quantity: 0,
      unitOfMeasure: '',
      psc: { code: o.psc_code ?? '', name: '' },
    }
  }

  if (source === 'Grants.gov') {
    return {
      ...baseSolicitation,
      source: 'Grants.gov' as const,
      opportunityCategory: '',
      fundingInstrumentType: '',
      categoryOfFundingActivity: '',
      cfdaNumbers: [],
      eligibleApplicants: '',
      costSharingRequired: false,
      agencyCode: '',
      estimatedTotalProgramFunding: 0,
      awardCeiling: 0,
      awardFloor: 0,
    }
  }

  return {
    ...baseSolicitation,
    source: 'SAM.gov' as const,
    noticeType: o.notice_type ?? '',
    naics: { code: o.naics_code ?? '', name: '' },
    psc: { code: o.psc_code ?? '', name: '' },
    setAside: o.set_aside_code ?? '',
    office: '',
  }
}

function transformSubawardDetailResponse(
  r: ApiSubawardDetailResponse,
): SubAwardDetail {
  return {
    subawardId: r.subaward_id,
    subawardNumber: r.subaward_number,
    internalId: r.internal_id,
    subawardType: r.subaward_type,
    amount: r.subaward_amount,
    date: r.sub_action_date,
    description: r.subaward_description,
    fiscalYear: r.sub_fiscal_year,

    subawardee: {
      id:
        buildRecipientId(
          r.subawardee.recipient_hash,
          selectRecipientLevel(null, r.subawardee.parent_uei),
        ) ??
        r.subawardee.uei ??
        '',
      name: r.subawardee.name ?? '',
      uei: r.subawardee.uei ?? '',
      duns: r.subawardee.duns,
      location: {
        city: r.subawardee.location.city ?? '',
        state: r.subawardee.location.state_code ?? '',
        zip: r.subawardee.location.zip5 ?? '',
        country: r.subawardee.location.country_name ?? 'USA',
        congressionalDistrict: r.subawardee.location.congressional_district,
      },
      businessTypes: r.subawardee.business_types ?? [],
      dbaName: r.subawardee.dba_name,
      parentName: r.subawardee.parent_name,
      parentUei: r.subawardee.parent_uei,
    },

    subawardeeOfficers: r.subawardee_officers.map((o) => ({
      fullName: o.name,
      email: '',
    })),

    primeAward: {
      awardId: r.prime_award.award_id,
      uniqueAwardKey: r.prime_award.unique_award_key,
      piid: r.prime_award.piid,
      fain: r.prime_award.fain,
      amount: r.prime_award.award_amount,
      description: r.prime_award.award_description,
      awardType: r.prime_award.prime_award_type,
      awardGroup: r.prime_award.prime_award_group,
    },

    primeContractor: {
      name: r.prime_contractor.name,
      uei: r.prime_contractor.uei,
      duns: r.prime_contractor.duns,
      id:
        buildRecipientId(
          r.prime_contractor.recipient_hash,
          selectRecipientLevel(null, r.prime_contractor.parent_uei),
        ) ??
        r.prime_contractor.uei ??
        '',
      recipientHash: r.prime_contractor.recipient_hash,
      dbaName: r.prime_contractor.dba_name,
      parentName: r.prime_contractor.parent_name,
      parentUei: r.prime_contractor.parent_uei,
      location: {
        city: r.prime_contractor.location.city ?? '',
        state: r.prime_contractor.location.state_code ?? '',
        zip: r.prime_contractor.location.zip5 ?? '',
        country: r.prime_contractor.location.country_name ?? 'USA',
      },
    },

    awardingAgency: {
      id: r.awarding_agency.toptier_code ?? '',
      name: r.awarding_agency.toptier_name ?? '',
      subTier: r.awarding_agency.subtier_name ?? '',
      office: r.awarding_agency.office_name ?? '',
    },

    fundingAgency: {
      id: r.funding_agency.toptier_code ?? '',
      name: r.funding_agency.toptier_name ?? '',
      subTier: r.funding_agency.subtier_name ?? '',
      office: r.funding_agency.office_name ?? '',
    },

    placeOfPerformance: {
      city: r.place_of_performance.city ?? '',
      state: r.place_of_performance.state_code ?? '',
      zip: r.place_of_performance.zip5 ?? '',
      country: r.place_of_performance.country_name ?? 'USA',
      congressionalDistrict: r.place_of_performance.congressional_district,
    },

    classification: {
      naicsCode: r.classification.naics_code,
      naicsDescription: r.classification.naics_description,
      pscCode: r.classification.product_or_service_code,
      pscDescription: r.classification.product_or_service_description,
      cfdaNumber: r.classification.cfda_number,
      cfdaTitle: r.classification.cfda_title,
      contractPricingType: r.classification.type_of_contract_pricing,
      setAsideType: r.classification.type_set_aside,
      extentCompeted: r.classification.extent_competed,
    },

    reportYear: r.report_year,
    reportMonth: r.report_month,
    dateSubmitted: r.date_submitted,
    lastModifiedDate: r.last_modified_date,
  }
}

// ============================================================================

// ============================================================================

/** API response type for time series analytics (monthly or weekly granularity) */
interface ApiTimeSeriesResponse {
  entity_id: string
  results: Array<{
    year: number
    month: number // 1-12
    amount: string | number
    transaction_count: number
    date?: string
    description?: string
  }>
}

/** API response type for award types analytics */
interface ApiAwardTypesResponse {
  entity_id: string
  fiscal_year?: number
  results: Array<{
    category: string | null
    amount: string | number
    percentage: string | number
    award_count: number
  }>
}

/** API response type for geography analytics */
interface ApiGeographyResponse {
  entity_id: string
  fiscal_year?: number
  results: Array<{
    state_code: string | null
    state_name: string | null
    amount: string | number
    percentage: string | number
    award_count: number
  }>
}

interface ApiGeographyCitiesResponse {
  entity_id: string
  fiscal_year?: number
  results: Array<{
    city_name: string
    state_code: string
    state_name: string | null
    amount: string | number
    percentage: string | number
    award_count: number
    latitude: string | number
    longitude: string | number
  }>
}

/** API response type for set-asides analytics */
interface ApiSetAsidesResponse {
  entity_id: string
  fiscal_year?: number
  results: Array<{
    type_code: string | null
    type_name: string
    amount: string | number
    percentage: string | number
    award_count: number
  }>
}

/** API response type for competition analytics */
interface ApiCompetitionResponse {
  entity_id: string
  fiscal_year?: number
  results: Array<{
    extent_competed: string | null
    description: string
    amount: string | number
    percentage: string | number
    award_count: number
  }>
}

/** API response type for contract sizes analytics */
interface ApiContractSizesResponse {
  entity_id: string
  fiscal_year?: number
  results: Array<{
    bucket: string
    min_value: number
    max_value: number
    count: number
    total_amount: string | number
  }>
}

// --- Recipient Analytics Fetch Functions ---

function appendRecipientFiscalYear(url: URL, fiscalYear?: number) {
  if (fiscalYear && !url.searchParams.has('fiscal_year')) {
    url.searchParams.set('fiscal_year', String(fiscalYear))
  }
}

function appendRecipientGeographyFilters(
  url: URL,
  filters?: RecipientAnalyticsFilters,
) {
  if (!filters) return

  if (filters.awardCategory) {
    url.searchParams.set('award_category', filters.awardCategory)
  }
  if (filters.popStateCode) {
    url.searchParams.set('pop_state_code', filters.popStateCode)
  }
  if (filters.awardingAgencyCode) {
    url.searchParams.set('awarding_agency_code', filters.awardingAgencyCode)
  }
  if (filters.naicsCode) {
    url.searchParams.set('naics_code', filters.naicsCode)
  }
  if (filters.pscCode) {
    url.searchParams.set('psc_code', filters.pscCode)
  }
  if (filters.typeSetAside) {
    url.searchParams.set('type_set_aside', filters.typeSetAside)
  }
  if (filters.extentCompeted) {
    url.searchParams.set('extent_competed', filters.extentCompeted)
  }
}

export async function fetchRecipientTimeSeries(
  token: string,
  recipientId: string,
  granularity?: string,
): Promise<TimeSeriesPoint[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/time-series`,
  )
  if (granularity && granularity !== 'monthly') {
    url.searchParams.set('granularity', granularity)
  }
  const response = await fetchJson<ApiTimeSeriesResponse>(token, url.toString())
  return response.results.map((r) => ({
    year: r.year,
    month: r.month,
    amount: Number(r.amount),
    transactionCount: r.transaction_count,
    date: r.date,
  }))
}

export async function fetchRecipientAwardTypes(
  token: string,
  recipientId: string,
  fiscalYear?: number,
): Promise<CategoryBreakdown[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/award-types`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))

  const response = await fetchJson<ApiAwardTypesResponse>(token, url.toString())
  return response.results.map((r) => ({
    category: r.category as AwardCategory | null,
    amount: Number(r.amount),
    percentage: Number(r.percentage),
    awardCount: r.award_count,
  }))
}

export async function fetchRecipientGeography(
  token: string,
  recipientId: string,
  fiscalYear?: number,
  limit: number = 51,
  filters?: RecipientAnalyticsFilters,
): Promise<GeographicDistribution[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/geography`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))
  url.searchParams.set('limit', String(limit))
  appendRecipientGeographyFilters(url, filters)

  const response = await fetchJson<ApiGeographyResponse>(token, url.toString())
  return response.results.map((r) => ({
    stateCode: r.state_code,
    stateName: r.state_name,
    amount: Number(r.amount) || 0,
    percentage: Number(r.percentage) || 0,
    awardCount: r.award_count,
  }))
}

export async function fetchRecipientGeographyCities(
  token: string,
  recipientId: string,
  fiscalYear?: number,
  limit: number = 250,
  filters?: RecipientAnalyticsFilters,
): Promise<GeographicCityDistribution[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/geography/cities`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))
  url.searchParams.set('limit', String(limit))
  appendRecipientGeographyFilters(url, filters)

  const response = await fetchJson<ApiGeographyCitiesResponse>(
    token,
    url.toString(),
  )
  return response.results
    .map((r) => ({
      cityName: r.city_name,
      stateCode: r.state_code,
      stateName: r.state_name,
      amount: Number(r.amount) || 0,
      percentage: Number(r.percentage) || 0,
      awardCount: r.award_count,
      latitude: Number(r.latitude),
      longitude: Number(r.longitude),
    }))
    .filter((r) => Number.isFinite(r.latitude) && Number.isFinite(r.longitude))
}

export async function fetchRecipientSetAsides(
  token: string,
  recipientId: string,
  fiscalYear?: number,
): Promise<SetAsideBreakdown[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/set-asides`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))

  const response = await fetchJson<ApiSetAsidesResponse>(token, url.toString())
  return response.results.map((r) => ({
    typeCode: r.type_code,
    typeName: r.type_name,
    amount: Number(r.amount),
    percentage: Number(r.percentage),
    awardCount: r.award_count,
  }))
}

export async function fetchRecipientCompetition(
  token: string,
  recipientId: string,
  fiscalYear?: number,
): Promise<CompetitionBreakdown[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/competition`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))

  const response = await fetchJson<ApiCompetitionResponse>(
    token,
    url.toString(),
  )
  return response.results.map((r) => ({
    extentCompeted: r.extent_competed,
    description: r.description,
    amount: Number(r.amount),
    percentage: Number(r.percentage),
    awardCount: r.award_count,
  }))
}

export async function fetchRecipientContractSizes(
  token: string,
  recipientId: string,
  fiscalYear?: number,
): Promise<ContractSizeHistogram[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/contract-sizes`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))

  const response = await fetchJson<ApiContractSizesResponse>(
    token,
    url.toString(),
  )
  return response.results.map((r) => ({
    bucket: r.bucket,
    minValue: r.min_value,
    maxValue: r.max_value,
    count: r.count,
    totalAmount: Number(r.total_amount),
  }))
}

/** Raw API response for the dashboard endpoint */
interface ApiRecipientDashboardResponse {
  recipient_hash: string
  fiscal_year: number | null
  deferred_fields: string[]
  stats: {
    total_prime_awards: number
    total_obligated_amount: string | number
    last_transaction_date: string | null
  } | null
  time_series: Array<{
    year: number
    month: number
    amount: string | number
    transaction_count: number
  }> | null
  top_agencies: ApiAnalyticsCategoryItem[] | null
  top_naics: ApiAnalyticsCodeCategoryItem[] | null
  top_psc: ApiAnalyticsCodeCategoryItem[] | null
  award_types: Array<{
    category: string | null
    amount: string | number
    percentage: string | number
    award_count: number
  }> | null
  geography: Array<{
    state_code: string | null
    state_name: string | null
    amount: string | number
    percentage: string | number
    award_count: number
  }> | null
  set_asides: Array<{
    type_code: string | null
    type_name: string
    amount: string | number
    percentage: string | number
    award_count: number
  }> | null
  competition: Array<{
    extent_competed: string | null
    description: string
    amount: string | number
    percentage: string | number
    award_count: number
  }> | null
  contract_sizes: Array<{
    bucket: string
    min_value: number
    max_value: number
    count: number
    total_amount: string | number
  }> | null
  insights: RecipientInsight[] | null
}

export async function fetchRecipientDashboard(
  token: string,
  recipientId: string,
  fiscalYear?: number,
  includePsc?: boolean,
): Promise<RecipientDashboard> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/dashboard`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))
  if (includePsc) url.searchParams.set('include_psc', 'true')

  const r = await fetchJson<ApiRecipientDashboardResponse>(
    token,
    url.toString(),
  )

  return {
    recipientHash: r.recipient_hash,
    fiscalYear: r.fiscal_year,
    deferredFields: r.deferred_fields ?? [],
    stats: r.stats
      ? {
          totalPrimeAwards: r.stats.total_prime_awards,
          totalObligatedAmount: Number(r.stats.total_obligated_amount),
          lastTransactionDate: r.stats.last_transaction_date,
        }
      : null,
    timeSeries:
      r.time_series?.map((t) => ({
        year: t.year,
        month: t.month,
        amount: Number(t.amount),
        transactionCount: t.transaction_count,
      })) ?? null,
    topAgencies:
      r.top_agencies?.map((a) => ({
        name: a.name,
        code: a.agency_code,
        amount: a.amount,
        percentage: a.percentage,
      })) ?? null,
    topNaics:
      r.top_naics?.map((n) => ({
        name: n.description || n.code,
        code: n.code,
        amount: n.amount,
        percentage: n.percentage,
      })) ?? null,
    topPsc:
      r.top_psc?.map((p) => ({
        name: p.description || p.code,
        code: p.code,
        amount: p.amount,
        percentage: p.percentage,
      })) ?? null,
    awardTypes:
      r.award_types?.map((a) => ({
        category: a.category as AwardCategory | null,
        amount: Number(a.amount),
        percentage: Number(a.percentage),
        awardCount: a.award_count,
      })) ?? null,
    geography:
      r.geography?.map((g) => ({
        stateCode: g.state_code,
        stateName: g.state_name,
        amount: Number(g.amount),
        percentage: Number(g.percentage),
        awardCount: g.award_count,
      })) ?? null,
    setAsides:
      r.set_asides?.map((s) => ({
        typeCode: s.type_code,
        typeName: s.type_name,
        amount: Number(s.amount),
        percentage: Number(s.percentage),
        awardCount: s.award_count,
      })) ?? null,
    competition:
      r.competition?.map((c) => ({
        extentCompeted: c.extent_competed,
        description: c.description,
        amount: Number(c.amount),
        percentage: Number(c.percentage),
        awardCount: c.award_count,
      })) ?? null,
    contractSizes:
      r.contract_sizes?.map((cs) => ({
        bucket: cs.bucket,
        minValue: cs.min_value,
        maxValue: cs.max_value,
        count: cs.count,
        totalAmount: Number(cs.total_amount),
      })) ?? null,
    insights: r.insights ?? null,
  }
}

export async function fetchRecipientCeilingTimeSeries(
  token: string,
  recipientId: string,
): Promise<TimeSeriesPoint[]> {
  const response = await fetchJson<ApiTimeSeriesResponse>(
    token,
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/ceiling-time-series`,
  )
  return response.results.map((r) => ({
    year: r.year,
    month: r.month,
    amount: Number(r.amount),
    transactionCount: r.transaction_count,
    date: r.date,
  }))
}

/** API response type for IDV group time series */
interface ApiIdvChildTimeSeries {
  award_id: string
  points: Array<{
    year: number
    month: number
    amount: string | number
    transaction_count: number
    date?: string
  }>
}

interface ApiIdvGroupTimeSeriesResponse {
  parent_piid: string
  combined: Array<{
    year: number
    month: number
    amount: string | number
    transaction_count: number
    date?: string
  }>
  children: ApiIdvChildTimeSeries[]
}

export async function fetchIdvGroupTimeSeries(
  token: string,
  recipientId: string,
  parentPiid: string,
  granularity?: string,
): Promise<IdvGroupTimeSeriesResponse> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/idv-time-series`,
  )
  url.searchParams.set('parent_piid', parentPiid)
  if (granularity && granularity !== 'monthly') {
    url.searchParams.set('granularity', granularity)
  }

  const response = await fetchJson<ApiIdvGroupTimeSeriesResponse>(
    token,
    url.toString(),
  )
  return {
    parentPiid: response.parent_piid,
    combined: response.combined.map((r) => ({
      year: r.year,
      month: r.month,
      amount: Number(r.amount),
      transactionCount: r.transaction_count,
      date: r.date,
    })),
    children: response.children.map((c) => ({
      awardId: c.award_id,
      points: c.points.map((r) => ({
        year: r.year,
        month: r.month,
        amount: Number(r.amount),
        transactionCount: r.transaction_count,
        date: r.date,
      })),
    })),
  }
}

// --- Agency Analytics Fetch Functions ---

export async function fetchAgencyTimeSeries(
  token: string,
  agencyCode: string,
): Promise<TimeSeriesPoint[]> {
  const response = await fetchJson<ApiTimeSeriesResponse>(
    token,
    `${API_URL}/api/v1/usaspending/agencies/${agencyCode}/analytics/time-series`,
  )
  return response.results.map((r) => ({
    year: r.year,
    month: r.month,
    amount: Number(r.amount),
    transactionCount: r.transaction_count,
  }))
}

export async function fetchAgencyAwardTypes(
  token: string,
  agencyCode: string,
  fiscalYear?: number,
): Promise<CategoryBreakdown[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/agencies/${agencyCode}/analytics/award-types`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))

  const response = await fetchJson<ApiAwardTypesResponse>(token, url.toString())
  return response.results.map((r) => ({
    category: r.category as AwardCategory | null,
    amount: Number(r.amount),
    percentage: Number(r.percentage),
    awardCount: r.award_count,
  }))
}

export async function fetchAgencyGeography(
  token: string,
  agencyCode: string,
  fiscalYear?: number,
  limit: number = 51,
): Promise<GeographicDistribution[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/agencies/${agencyCode}/analytics/geography`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))
  url.searchParams.set('limit', String(limit))

  const response = await fetchJson<ApiGeographyResponse>(token, url.toString())
  return response.results.map((r) => ({
    stateCode: r.state_code,
    stateName: r.state_name,
    amount: Number(r.amount),
    percentage: Number(r.percentage),
    awardCount: r.award_count,
  }))
}

export async function fetchAgencySetAsides(
  token: string,
  agencyCode: string,
  fiscalYear?: number,
): Promise<SetAsideBreakdown[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/agencies/${agencyCode}/analytics/set-asides`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))

  const response = await fetchJson<ApiSetAsidesResponse>(token, url.toString())
  return response.results.map((r) => ({
    typeCode: r.type_code,
    typeName: r.type_name,
    amount: Number(r.amount),
    percentage: Number(r.percentage),
    awardCount: r.award_count,
  }))
}

export async function fetchAgencyTopPscCodes(
  token: string,
  agencyCode: string,
  fiscalYear?: number,
  limit: number = 10,
): Promise<(TopCategory & { code: string })[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/agencies/${agencyCode}/analytics/psc`,
  )
  if (fiscalYear) url.searchParams.set('fiscal_year', String(fiscalYear))
  url.searchParams.set('limit', String(limit))

  interface ApiAgencyPscResponse {
    recipient_hash: string
    fiscal_year?: number
    results: Array<{
      code: string
      description?: string
      amount: string | number
      percentage: string | number
    }>
  }

  const response = await fetchJson<ApiAgencyPscResponse>(token, url.toString())
  return response.results.map((r) => ({
    name: r.description || r.code,
    code: r.code,
    amount: Number(r.amount),
    percentage: Number(r.percentage),
  }))
}

// --- Award Cash Flow Fetch Functions ---

export async function fetchRecipientAwardPortfolio(
  token: string,
  recipientId: string,
): Promise<AwardPortfolioResponse> {
  const raw = await fetchJson<AwardPortfolioResponse>(
    token,
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/award-portfolio?limit=5000`,
  )
  // Backend Decimal fields serialize as strings — coerce to numbers.
  return {
    ...raw,
    total_obligation: Number(raw.total_obligation),
    total_ceiling: raw.total_ceiling != null ? Number(raw.total_ceiling) : null,
    total_outlays: raw.total_outlays != null ? Number(raw.total_outlays) : null,
  }
}

export async function fetchRecipientOutlayTimeSeries(
  token: string,
  recipientId: string,
): Promise<TimeSeriesPoint[]> {
  const response = await fetchJson<{
    recipient_id: string
    results: Array<{
      year: number
      month: number
      amount: string | number
      date?: string
    }>
  }>(
    token,
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/analytics/outlay-time-series`,
  )
  return response.results.map((r) => ({
    year: r.year,
    month: r.month,
    amount: Number(r.amount),
    transactionCount: 0, // outlay data has no transaction count
    date: r.date,
  }))
}

export async function fetchAwardTimeSeries(
  token: string,
  awardId: string,
  granularity?: string,
): Promise<TimeSeriesPoint[]> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/awards/${awardId}/analytics/time-series`,
  )
  if (granularity && granularity !== 'monthly') {
    url.searchParams.set('granularity', granularity)
  }
  const response = await fetchJson<ApiTimeSeriesResponse>(token, url.toString())
  return response.results.map((r) => ({
    year: r.year,
    month: r.month,
    amount: Number(r.amount),
    transactionCount: r.transaction_count,
    date: r.date,
    description: r.description,
  }))
}

export async function fetchIdvParent(
  token: string,
  awardId: string,
): Promise<IdvParentItem | null> {
  const response = await fetchJson<{
    award_id: string
    result: IdvParentItem | null
  }>(token, `${API_URL}/api/v1/usaspending/awards/${awardId}/idv-parent`)
  return response.result
}

// =============================================================================
// Public (unauthenticated) fetchers for ISR / getStaticProps
// =============================================================================

/**
 * Fetch recipient basic info without auth token (for server-side ISR).
 * Same data as fetchRecipientBasicInfo but uses plain fetch without Authorization header.
 */
export async function fetchRecipientBasicInfoPublic(
  id: string,
): Promise<RecipientProfile | null> {
  const res = await fetch(
    `${API_URL}/api/v1/usaspending/recipients/${id}/basic-info`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  if (res.status === 404) return null
  if (!res.ok) throw res
  const response: ApiRecipientBasicInfoResponse = await res.json()
  return {
    id:
      response.recipient_id ??
      buildRecipientId(response.recipient_hash, response.recipient_level) ??
      '',
    name: response.legal_business_name ?? '',
    uei: response.uei ?? '',
    duns: response.duns,
    availableLevels: (response.available_levels ?? []) as RecipientLevel[],
    selfReferential: response.self_referential ?? false,
    parent: response.parent
      ? {
          name: response.parent.name ?? '',
          // Parent link defaults to P-level; may 404 if entity lacks a P record (rare)
          id: buildRecipientId(response.parent.recipient_hash, 'P'),
        }
      : undefined,
    location: {
      streetAddress: response.address?.line_1,
      city: response.address?.city ?? '',
      state: response.address?.state ?? '',
      zip: response.address?.zip5 ?? '',
      country: response.address?.country_code ?? 'USA',
    },
    businessTypes: [],
    stats: undefined,
    topAgencies: [],
    topPscCodes: [],
    recentAwards: [],
  }
}

/**
 * Fetch recipient dashboard without auth token (for server-side ISR).
 * Same data as fetchRecipientDashboard but uses plain fetch without Authorization header.
 */
export async function fetchRecipientDashboardPublic(
  recipientId: string,
): Promise<RecipientDashboard | null> {
  const res = await fetch(
    `${API_URL}/api/v1/usaspending/recipients/${recipientId}/dashboard`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  if (res.status === 404) return null
  if (!res.ok) throw res
  const r: ApiRecipientDashboardResponse = await res.json()

  return {
    recipientHash: r.recipient_hash,
    fiscalYear: r.fiscal_year,
    deferredFields: r.deferred_fields ?? [],
    stats: r.stats
      ? {
          totalPrimeAwards: r.stats.total_prime_awards,
          totalObligatedAmount: Number(r.stats.total_obligated_amount),
          lastTransactionDate: r.stats.last_transaction_date,
        }
      : null,
    timeSeries:
      r.time_series?.map((t) => ({
        year: t.year,
        month: t.month,
        amount: Number(t.amount),
        transactionCount: t.transaction_count,
      })) ?? null,
    topAgencies:
      r.top_agencies?.map((a) => ({
        name: a.name,
        code: a.agency_code,
        amount: a.amount,
        percentage: a.percentage,
      })) ?? null,
    topNaics:
      r.top_naics?.map((n) => ({
        name: n.description || n.code,
        code: n.code,
        amount: n.amount,
        percentage: n.percentage,
      })) ?? null,
    topPsc:
      r.top_psc?.map((p) => ({
        name: p.description || p.code,
        code: p.code,
        amount: p.amount,
        percentage: p.percentage,
      })) ?? null,
    awardTypes:
      r.award_types?.map((a) => ({
        category: a.category as AwardCategory | null,
        amount: Number(a.amount),
        percentage: Number(a.percentage),
        awardCount: a.award_count,
      })) ?? null,
    geography:
      r.geography?.map((g) => ({
        stateCode: g.state_code,
        stateName: g.state_name,
        amount: Number(g.amount),
        percentage: Number(g.percentage),
        awardCount: g.award_count,
      })) ?? null,
    setAsides:
      r.set_asides?.map((s) => ({
        typeCode: s.type_code,
        typeName: s.type_name,
        amount: Number(s.amount),
        percentage: Number(s.percentage),
        awardCount: s.award_count,
      })) ?? null,
    competition:
      r.competition?.map((c) => ({
        extentCompeted: c.extent_competed,
        description: c.description,
        amount: Number(c.amount),
        percentage: Number(c.percentage),
        awardCount: c.award_count,
      })) ?? null,
    contractSizes:
      r.contract_sizes?.map((cs) => ({
        bucket: cs.bucket,
        minValue: cs.min_value,
        maxValue: cs.max_value,
        count: cs.count,
        totalAmount: Number(cs.total_amount),
      })) ?? null,
    insights: r.insights ?? null,
  }
}
